<script lang="ts" setup>
import { computed, Ref, ref, watch } from "vue";
import { Image } from "vant";
import api from "@/modules/api";
import CheckCircleFillIcon from "@/components/icon/CheckCircleFillIcon.vue";
import MateMediaPreview from "@/components/mobile/overlay/MateMediaPreview.vue";
import { isAudio, isImage, isVideo } from "@/modules/common";
import ScrollLoadingList, {
  ScrollLoadingListInstance,
} from "@/components/mobile/ScrollLoadingList.vue";
import BaseImg from "../common/BaseImg.vue";
import {
  MediaResourceType,
  ModuleEntityType,
  ModuleMedia,
} from "@/types/entity";
import { useMate } from "@/stores/mate";
// 1. 常量
// 2. Props & Emit & Expose
export interface ModuleGalleryInstance {
  refresh: (clearFilter?: boolean) => Promise<void>;
  selectedMediaList: Ref<ModuleMedia[]>;
}

const props = withDefaults(
  defineProps<{
    entityId: number;
    entityType: ModuleEntityType;
    filter?: MediaResourceType; // 1-图片；2-视频；3-音频
    selectable?: boolean;
    enableUpload?: boolean;
    maxSelectCount?: number;
    scroller?: Element | null;
  }>(),
  {
    selectable: false,
    enableUpload: false,
    maxSelectCount: 1,
  }
);

const mateStore = useMate();
// 3. 内部状态
const currentFilter = ref<MediaResourceType | null>(props.filter ?? null); // null: 全部, 1: 图片, 2: 视频, 3: 音频
const allMediaList = ref<ModuleMedia[]>([]); // 所有媒体列表
// 实际渲染的媒体列表（根据过滤器）
const mediaList = computed<ModuleMedia[]>(() => {
  let list = [] as ModuleMedia[];
  let idMap = new Set<number>();
  allMediaList.value.forEach((v) => {
    if (idMap.has(v.id!)) {
      return;
    }
    idMap.add(v.id!);
    list.push(v);
  });
  return list;
});
// 预览遮罩层状态
const isShowPreview = ref(false);
const startPosition = ref(0);
// 选中状态
const selectedMediaIdSet = ref<Set<number>>(new Set());
const selectedMediaList = computed<ModuleMedia[]>(() => {
  return mediaList.value.filter((item) =>
    selectedMediaIdSet.value.has(item.id!)
  );
});
const scrollListRef = ref<ScrollLoadingListInstance | null>(null);
// 4. Lifecycle
const scrollLoad = async (reload = false) => {
  let cursor = "";
  if (!reload && allMediaList.value.length > 0) {
    cursor = btoa(
      JSON.stringify({
        id: allMediaList.value[allMediaList.value.length - 1].id,
        _pointsToNextItems: true,
      })
    );
  }
  const { page } = await api.readModuleMediaPage({
    filter: {
      entityId: props.entityId!,
      entityType: props.entityType!,
      type: currentFilter.value === null ? undefined : currentFilter.value,
    },
    pagination: { page: 1, perPage: 15 },
    sort: { field: "id", order: "desc" },
    cursor,
  });

  allMediaList.value.push(...page.data);
  if (page.data.length < 15) {
    return false;
  }
  return true;
};
watch(currentFilter, () => {
  allMediaList.value = [];
  scrollListRef.value?.resetScrollLoadingState();
});

// 5. Methods
/** 从服务端重新获取gallery列表 */
async function refresh(clearFilter = false) {
  allMediaList.value = [];
  if (clearFilter) {
    currentFilter.value = null;
  }
  scrollListRef.value?.resetScrollLoadingState();
}

const onSelect = (idx: number) => {
  if (props.selectable) {
    const target = mediaList.value[idx];
    if (props.maxSelectCount === 1) {
      selectedMediaIdSet.value = new Set([target.id!]);
      return;
    }
    if (
      !selectedMediaIdSet.value.has(target.id!) &&
      selectedMediaList.value.length >= props.maxSelectCount
    )
      return;
    if (selectedMediaIdSet.value.has(target.id!)) {
      selectedMediaIdSet.value.delete(target.id!);
    } else {
      selectedMediaIdSet.value.add(target.id!);
    }
  } else {
    isShowPreview.value = true;
    startPosition.value = idx;
  }
};
const afterMateMediaDelete = (mediaId: number) => {
  const index = allMediaList.value.findIndex(
    (item) => item.media.id === mediaId
  );
  allMediaList.value.splice(index, 1);
};

defineExpose<ModuleGalleryInstance>({
  refresh, // method: 刷新gallery列表
  selectedMediaList, // computed: 已选中的媒体列表
});

const isBottomShow = ref(false);
</script>
<template>
  <div class="flex flex-col gap-2 h-full">
    <div v-if="!filter" class="flex justify-between items-center">
      <div class="tabs tabs-boxed w-fit rounded">
        <a
          :class="{ 'tab-active': currentFilter === null }"
          class="tab tab-xs"
          @click="currentFilter = null"
        >
          {{ $t("User.gallery.all") }}
        </a>
        <a
          :class="{ 'tab-active': currentFilter === 1 }"
          class="tab tab-xs"
          @click="currentFilter = 1"
        >
          <i class="iconify mingcute--pic-line" />
        </a>
        <a
          :class="{ 'tab-active': currentFilter === 2 }"
          class="tab tab-xs"
          @click="currentFilter = 2"
        >
          <i class="iconify mingcute--video-camera-line" />
        </a>
        <a
          :class="{ 'tab-active': currentFilter === 3 }"
          class="tab tab-xs"
          @click="currentFilter = 3"
        >
          <i class="iconify mingcute--music-line"></i>
        </a>
      </div>

      <button
        class="btn btn-primary-content btn-xs rounded"
        @click="isBottomShow = true"
      >
        <plus-icon class="icon-base" />
        {{ $t("Buttons.add") }}
      </button>
    </div>
    <scroll-loading-list
      ref="scrollListRef"
      :on-scroll-load="scrollLoad"
      :scroller="scroller"
      class="min-h-full"
    >
      <div class="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-0.5">
        <div
          v-for="(item, idx) in mediaList"
          :key="item.id"
          class="w-full aspect-[1/1] overflow-hidden"
          @click="onSelect(idx)"
        >
          <div
            v-if="isImage(item.media.url)"
            class="bg-base-200 w-full h-full relative flex items-center justify-center"
          >
            <BaseImg :media="item.media" :width="200" class="h-full" />
            <div
              v-if="selectedMediaIdSet.has(item.id!)"
              class="backdrop-filter backdrop-blur-sm absolute inset-0 bg-neutral-900 opacity-50"
            ></div>
            <check-circle-fill-icon
              v-if="selectedMediaIdSet.has(item.id!)"
              class="icon-base right-1 bottom-1 absolute text-info"
            />
          </div>
          <div
            v-else-if="isVideo(item.media.url)"
            class="w-full h-full bg-base-200 relative"
          >
            <Image
              :src="item.media.videoCoverUrl!"
              class="w-full h-full"
              fit="cover"
              crossorigin="anonymous"
            />
            <div
              class="absolute right-1 bottom-1 bg-neutral p-1 rounded flex items-center justify-center opacity-80"
            >
              <i
                class="iconify mingcute--video-camera-line text-neutral-content"
              />
            </div>
            <div
              v-if="selectedMediaIdSet.has(item.id!)"
              class="backdrop-filter backdrop-blur-sm absolute inset-0 bg-neutral-900 opacity-50"
            ></div>
            <check-circle-fill-icon
              v-if="selectedMediaIdSet.has(item.id!)"
              class="icon-base right-1 bottom-1 absolute text-info"
            />
          </div>
          <div
            v-else-if="isAudio(item.media.url)"
            class="w-full h-full bg-base-200 relative"
          >
            <div class="flex justify-center items-center h-full relative">
              <i class="iconify mingcute--play-circle-fill"></i>

              <div
                class="absolute right-1 bottom-1 bg-neutral p-1 rounded flex items-center justify-center opacity-50"
              >
                <i
                  class="iconify mingcute--music-line text-neutral-content"
                ></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </scroll-loading-list>
    <mate-media-preview
      v-model:show="isShowPreview"
      enable-delete
      media-module="module"
      :mate-media-list="
        mediaList.map((v) => ({
          ...v.media,
          moduleMediaId: v.id,
        }))
      "
      :start-position="startPosition"
      @after-delete="afterMateMediaDelete"
    />

    <media-upload-popup
      v-model:show="isBottomShow"
      :mate-id="mateStore.userMate.id"
      :entity-id="entityId!"
      :entity-type="entityType!"
      @finished="refresh"
    />
  </div>
</template>
<style>
.media-preview .van-image-preview__cover {
  top: unset;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
